import { useEffect, useMemo, useRef, useState } from 'react'
import Spinner from '../../../Components/LoadingSpinner'
import ActionBar from '../../../Components/Recommendations/ActionBar'
import { VenueRowSpace } from '../../../Components/Recommendations/VenueRow'
import useBriefContext from '../../../Hooks/Brief/useBriefContext'
import { useCreateFavourites } from '../../../Hooks/Brief/useCreateFavorites/useCreateFavorites'
import useSpaceRecommendations, {
  Space,
} from '../../../Hooks/useRecommendation/useSpaceRecommendations'
import { ShortlistSpace, useShortlistedSpaces } from '../../../Hooks/useShortlist'
import isDirectEnquiryBrief from '../../../Logic/Form/Sequences/DirectEnquiry/isDirectEnquiryBrief'
import { clearStoredShortlist } from '../../../Utilities/Storage/shortlist'
import { MAX_REQUESTED_VENUES } from '../../../Constants'
import { CrossIcon, GroupIcon, HeartIcon, LeftIcon, LocationIcon, RightIcon } from './Icons'
import './LeadFeedSuccess.less'
import '../../../Styles/buttons.less'

export interface SpaceInterest {
  spaceId: number
  hostId: number
}

// How many venues the success page shortlists on the guest's behalf, capped by the server maximum.
const PRESELECTED_VENUES = 5

const toSpaceCard = (space: ShortlistSpace): Space => ({
  ...space,
  location: { ...space.location, latlng: { lat: 0, lng: 0 } },
  description: '',
  capacity: 0,
  standingCapacity: null,
  seatingCapacity: null,
  googleRating: 0,
  googleReviews: 0,
  tags: [],
})

interface UseSpaceRecsArgs {
  // The success page starts with a shortlist already made; the step wants the guest to choose.
  preselect?: boolean
  skip?: boolean
}

interface UseSpaceRecs {
  loading: boolean
  error: string | null
  recommendations: Space[]
  requestedSpaces: SpaceInterest[]
  // Every requested space, recommended or shortlisted elsewhere, in the order they were added.
  shortlistedSpaces: ShortlistSpace[]
  hasArrivedWithShortlist: boolean
  onToggleSpace: (spaceId: number, hostId: number) => void
  maxRequestedVenues: number
  enquiryAccessSecret: string | undefined
  saveRequests: (forBriefId?: number) => Promise<void>
  needsChoice: boolean
  flagMinimumNotMet: () => void
}

// Shared by the recommendations step and the success page; only the timing vs submission differs.
export const useSpaceRecs = ({
  preselect = false,
  skip = false,
}: UseSpaceRecsArgs = {}): UseSpaceRecs => {
  const { brief } = useBriefContext()
  const { loading, error, recommendations } = useSpaceRecommendations(
    brief,
    undefined,
    undefined,
    undefined,
    undefined,
    skip
  )
  const {
    createFavourites,
    maxRequestedVenues: serverMax,
    enquiryAccessSecret,
  } = useCreateFavourites(brief.id)
  const [requestedSpaces, setRequestedSpaces] = useState<SpaceInterest[]>([])
  const { spaces: resolvedShortlist, fetching: shortlistFetching } = useShortlistedSpaces(
    recommendations,
    skip
  )
  const seededShortlist = useRef(false)
  const preselected = useRef(false)

  const enquiredSpaceId = isDirectEnquiryBrief(brief) ? Number(brief.spaceId) : null
  const shortlistSpaces = useMemo(
    () =>
      enquiredSpaceId === null
        ? resolvedShortlist
        : resolvedShortlist.filter(({ id }) => Number(id) !== enquiredSpaceId),
    [resolvedShortlist, enquiredSpaceId]
  )

  // Whichever cap is stricter - the product limit applies before the server query resolves.
  const maxRequestedVenues = Math.min(serverMax ?? MAX_REQUESTED_VENUES, MAX_REQUESTED_VENUES)

  const addRequests = (spaces: { id: string; hostID: number }[], limit: number) =>
    setRequestedSpaces((requested) => {
      const merged = [...requested]

      for (const space of spaces) {
        if (merged.length >= limit) break
        if (merged.some(({ spaceId }) => spaceId === Number(space.id))) continue

        merged.push({ spaceId: Number(space.id), hostId: space.hostID })
      }

      return merged
    })

  // What the guest chose before arriving outranks anything we would have picked for them.
  useEffect(() => {
    if (seededShortlist.current || !shortlistSpaces.length) return

    seededShortlist.current = true
    addRequests(shortlistSpaces, maxRequestedVenues)
  }, [shortlistSpaces, maxRequestedVenues])

  useEffect(() => {
    if (preselected.current || !preselect || !recommendations.length || shortlistFetching) return

    preselected.current = true
    addRequests(recommendations, Math.min(PRESELECTED_VENUES, maxRequestedVenues))
  }, [recommendations, maxRequestedVenues, shortlistFetching])

  const [needsChoice, setNeedsChoice] = useState(false)

  const onToggleSpace = (spaceId: number, hostId: number) => {
    setNeedsChoice(false)
    setRequestedSpaces((prev) =>
      prev.some((space) => space.spaceId === spaceId)
        ? prev.filter((space) => space.spaceId !== spaceId)
        : [...prev, { spaceId, hostId }]
    )
  }

  const saveRequests = async (forBriefId?: number) => {
    if (requestedSpaces.length) await createFavourites(requestedSpaces, forBriefId)

    // Consumed - a later brief must not inherit venues chosen for this one.
    clearStoredShortlist()
  }

  const shortlistedSpaces = useMemo(() => {
    const byId = new Map<number, ShortlistSpace>()
    for (const space of [...recommendations, ...shortlistSpaces]) byId.set(Number(space.id), space)

    return requestedSpaces
      .map(({ spaceId }) => byId.get(spaceId))
      .filter((space): space is ShortlistSpace => space !== undefined)
  }, [recommendations, shortlistSpaces, requestedSpaces])

  const orderedRecommendations = useMemo(() => {
    if (!shortlistSpaces.length) return recommendations

    const shortlisted = new Set(shortlistSpaces.map(({ id }) => Number(id)))
    const recById = new Map(recommendations.map((space) => [Number(space.id), space]))

    return [
      ...shortlistSpaces.map((space) => recById.get(Number(space.id)) ?? toSpaceCard(space)),
      ...recommendations.filter((space) => !shortlisted.has(Number(space.id))),
    ]
  }, [recommendations, shortlistSpaces])

  return {
    loading: loading || shortlistFetching,
    error,
    recommendations: orderedRecommendations,
    requestedSpaces,
    shortlistedSpaces,
    hasArrivedWithShortlist: shortlistSpaces.length > 0,
    onToggleSpace,
    maxRequestedVenues,
    enquiryAccessSecret,
    saveRequests,
    needsChoice,
    flagMinimumNotMet: () => setNeedsChoice(true),
  }
}

const Carousel = ({ images, alt }: { images: string[]; alt: string }): JSX.Element | null => {
  const [currentIndex, setCurrentIndex] = useState(0)
  return (
    <div className='space-recs-image-carousel'>
      <img src={images[currentIndex]} alt={`${alt} - ${currentIndex + 1}`} />
      {images.length > 1 && (
        <div className='space-recs-image-carousel-nav'>
          <button
            className='space-recs-image-carousel-nav-prev'
            onClick={() => setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
            aria-label='Previous image'>
            <LeftIcon height={32} width={32} />
          </button>
          <button
            className='space-recs-image-carousel-nav-next'
            onClick={() => setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
            aria-label='Next image'>
            <RightIcon height={32} width={32} />
          </button>
        </div>
      )}
    </div>
  )
}

interface SpaceRecsNavProps {
  requestedCount: number
  // 0 on the success page, where the enquiry has already gone out; 1 on the step.
  minRequested?: number
  label: string
  confirmDisabled?: boolean
  onConfirm: () => void
  busy?: boolean
  needsChoice?: boolean
  onBelowMinimum?: () => void
  shortlistedSpaces?: ShortlistSpace[]
  onRemoveVenue?: (space: ShortlistSpace) => void
  pinned?: VenueRowSpace | null
  countSuffix?: string
  defaultOpen?: boolean
}

const venueWord = (count: number): string => `venue${count === 1 ? '' : 's'}`

// Must render in Page's `footer` - `.page-content`'s transform would contain this fixed bar.
export const SpaceRecsNav = ({
  requestedCount,
  minRequested = 0,
  label,
  confirmDisabled = false,
  onConfirm,
  busy = false,
  needsChoice = false,
  onBelowMinimum,
  shortlistedSpaces,
  onRemoveVenue,
  pinned,
  countSuffix,
  defaultOpen = false,
}: SpaceRecsNavProps): JSX.Element => {
  const belowMinimum = requestedCount < minRequested

  // Said alongside the outlined choices, not before them.
  const statusText =
    needsChoice && belowMinimum
      ? `Choose at least ${minRequested} ${venueWord(minRequested)} to continue`
      : ''

  // Only screens with a minimum reserve the line the message animates into.
  const hasStatus = minRequested > 0

  const onClick = () => {
    // The minimum does not block the button - clicking it is how the guest finds out.
    if (!belowMinimum) return onConfirm()

    onBelowMinimum?.()
    window.scrollTo({ top: 0 })
  }

  return (
    <ActionBar
      className='recs-nav'
      onConfirm={onClick}
      confirmLabel={label}
      confirmDisabled={confirmDisabled}
      disabled={busy}
      shortlistedSpaces={shortlistedSpaces ?? []}
      onRemoveVenue={(space) => onRemoveVenue?.(space)}
      pinned={pinned}
      countSuffix={countSuffix}
      alwaysShowList={shortlistedSpaces !== undefined}
      defaultOpen={defaultOpen}
      status={
        hasStatus && (
          <p className={`recs-nav-status ${statusText ? 'show' : ''}`} aria-live='polite'>
            {statusText}
          </p>
        )
      }
    />
  )
}

interface SpaceRecsListProps {
  recommendations: Space[]
  loading: boolean
  requestedSpaces: SpaceInterest[]
  onToggleSpace: (spaceId: number, hostId: number) => void
  maxRequestedVenues: number
  emptyMessage?: string
  // Marks the unchosen actions after submission is attempted below the minimum.
  needsChoice?: boolean
}

const SpaceRecsList = ({
  recommendations,
  loading,
  requestedSpaces,
  onToggleSpace,
  maxRequestedVenues,
  emptyMessage = 'We could not find any venues matching your brief just yet.',
  needsChoice = false,
}: SpaceRecsListProps): JSX.Element => {
  const [page, setPage] = useState(1)
  const [rejectedSpaces, setRejectedSpaces] = useState<number[]>([])

  if (loading) return <Spinner />

  if (!recommendations.length) return <p>{emptyMessage}</p>

  return (
    <>
      <div className='space-recs'>
        {recommendations.slice(0, page * 6).map((space) => {
          const spaceId = Number(space.id)
          const isSelected = requestedSpaces.some(
            (requestedSpace) => requestedSpace.spaceId === spaceId
          )
          const isRejected = rejectedSpaces.some((requestedSpace) => requestedSpace === spaceId)
          const limitReached = requestedSpaces.length >= maxRequestedVenues

          return (
            <div key={space.id} className='space-recs-card'>
              <Carousel images={space.images} alt={`${space.title}, ${space.venueName}`} />
              <div className='space-recs-card-content'>
                {!!space.tags.length && (
                  <div className='space-recs-card-tags'>
                    {space.tags.map((tag) => (
                      <div
                        key={tag}
                        className={`space-recs-card-tag${
                          tag === 'Recommended' ? ' space-recs-card-tag-recommended' : ''
                        }`}>
                        {tag}
                      </div>
                    ))}
                  </div>
                )}
                <h3 className='space-recs-card-clamp'>
                  {space.title}, {space.venueName}
                </h3>
                <div className='space-recs-card-details'>
                  <p className='space-recs-card-text-md'>
                    <LocationIcon height={20} width={20} className='space-recs-icon' />{' '}
                    {space.location.city} {space.location.country}
                  </p>
                  {!!space.capacity && (
                    <p className='space-recs-card-text-md'>
                      <GroupIcon height={18} width={18} className='space-recs-icon' />{' '}
                      {space.capacity}
                    </p>
                  )}
                </div>
                {!!space.description && (
                  <p className='space-recs-card-clamp'>{space.description}</p>
                )}
                <div className='space-recs-card-actions'>
                  <button
                    disabled={limitReached && !isSelected}
                    onClick={() => {
                      if (!isSelected && limitReached) return

                      onToggleSpace(spaceId, space.hostID)
                      setRejectedSpaces((prev) =>
                        isSelected
                          ? [...prev, spaceId]
                          : prev.filter((rejectedSpace) => rejectedSpace !== spaceId)
                      )
                    }}
                    className={`hb-button button-small ${
                      isSelected ? 'primary-button' : 'secondary-button'
                    }${needsChoice && !isSelected ? ' space-recs-card-needs-choice' : ''}`}>
                    <HeartIcon height={18} width={18} />
                    {limitReached && !isSelected ? 'Limit Reached' : "I'm interested"}
                  </button>
                  <button
                    onClick={() => {
                      if (isSelected) onToggleSpace(spaceId, space.hostID)
                      setRejectedSpaces((prev) => [...prev, spaceId])
                    }}
                    className={`hb-button button-small secondary-button${
                      isRejected ? ' space-recs-card-rejected' : ''
                    }`}>
                    <CrossIcon height={18} width={18} />
                    Not for me
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <button
        className='hb-button button-small secondary-button'
        onClick={() => setPage(page + 1)}
        disabled={page * 6 >= recommendations.length}>
        + Load more
      </button>
    </>
  )
}

export default SpaceRecsList
